import { IAppliedPromotion } from 'data/entities/promotions';
import { ICommissionBreakdownObject } from '../../../data/entities/commission-breakdown';
import { IBookingBreakdown } from '../../../data/entities/event/booking';
import { channelFriendlyNames } from '../../../entities/contracts/constants';

export const valuesConverted = (breakdown: IBookingBreakdown) => {
	try {
		if (!breakdown.channel.converted_cash_receipt_currency)
			return false;
		return breakdown.channel.cash_receipt_currency !== breakdown.channel.converted_cash_receipt_currency;
	} catch (e) {
		return false;
	}
};

export const bookingSourceName = (bookingSource: string, admin: boolean): string => {
	const friendlyName = channelFriendlyNames[bookingSource];

	if (friendlyName) {
		// Indicate whether it is a RU or Direct booking
		if (admin && bookingSource.includes('booking.com')) {
			return bookingSource.includes('rentalsunited') ? 'Booking.com (via Rentals United)' : 'Booking.com (direct)';
		}

		return friendlyName;
	} else {
		return bookingSource;
	}
};

interface IFeeRow {
	name: string | undefined;
	rate: number | undefined | null;
	val: number | undefined;
	highlightText?: boolean;
	isPromotionalCommissionAdjustment?: boolean;
}
/**
 * Creates data for each row of fees table (break down of commission values)
 * @param breakdown
 */
export const generateFeesRows = (
	breakdown: IBookingBreakdown,
	appliedPromos: IAppliedPromotion[],
	commissionBreakdown: ICommissionBreakdownObject): IFeeRow[] => {
	if (!breakdown.travelnest) {
		return [];
	}

	const promotionalCommissionAdjustment = processCommissionBreakdown(breakdown, commissionBreakdown, appliedPromos);

	const rows: IFeeRow[] = [
		{
			name: 'Travelnest Fee',
			rate: breakdown.travelnest.presented_travelnest_rate,
			val: breakdown.travelnest.presented_travelnest_net_value
		},
		{
			name: 'Channel Fee',
			rate: breakdown.travelnest.presented_channel_rate,
			val: breakdown.travelnest.presented_channel_net_value
		},
		{
			name: 'Commission Adjustments',
			rate: breakdown.travelnest.total_adjustment_commission_rate,
			val: breakdown.travelnest.total_adjustment_commission_net_value,
			highlightText: true
		},
		{
			name: 'Total',
			rate: breakdown.travelnest.commission_rate,
			val: breakdown.travelnest.commission_net_value
		},
	];

	if (promotionalCommissionAdjustment.length > 0 && breakdown.travelnest.total_adjustment_commission_rate !== 0) {
		for (const promotionalAdjustment of promotionalCommissionAdjustment) {
			rows.splice(rows.length - 1, 0, promotionalAdjustment);
		}
	}

	if (breakdown.travelnest.commission_vat_value) {
		rows.push({
			name: 'VAT',
			rate: null,
			val: breakdown.travelnest.commission_vat_value
		});
	}

	rows.push({
		name: 'Total including VAT',
		rate: null,
		val: breakdown.travelnest.commission_value
	});

	return rows;
};

export const processCommissionBreakdown = (
	breakdown: IBookingBreakdown,
	commissionBreakdownObject: ICommissionBreakdownObject,
	appliedPromos: IAppliedPromotion[]) => {

	for (const [_, commissionBreakdown] of Object.entries(commissionBreakdownObject)) {

		const commissionAdjustments = commissionBreakdown.adjustments;

		if (commissionAdjustments.length > 0) {
			const promotionalAdjustment = commissionAdjustments.filter((adjustment) => {
				return appliedPromos.find(({ internal_reference }) => adjustment.reason === internal_reference);
			});

			const adjustmentToDisplay: IFeeRow[] = promotionalAdjustment.reduce((adjustmentArr, currAdjustment) => {
				const promotionTemplate: any = appliedPromos.find((promotion) => currAdjustment.reason === promotion.internal_reference);
				return [...adjustmentArr, {
					name: promotionTemplate.display_name,
					rate: currAdjustment.value,
					val: breakdown.owner?.effective_rent_value ? breakdown.owner?.effective_rent_value * currAdjustment.value : 0,
					highlightText: true,
					isPromotionalCommissionAdjustment: true
				}];
			}, []);

			return adjustmentToDisplay;
		}
	}
	return [];
};

export const formatCurrencyField = (currencySymbol: string, amount: number) => `${currencySymbol}${amount.toFixed(2)}`;
